/**
 * Notifications client — typed wrapper for `/api/v1/notifications/*`.
 *
 * The bell polls these. There's no websocket layer yet — polling at
 * 30s open / 2 min closed is fine for the workspace sizes we target.
 * If we hit > 50 active users in one workspace we'll switch to SSE
 * via the same EventSourceResponse pattern we already use elsewhere.
 */

import { api } from "./api";

export type NotificationKind =
  | "mention"
  | "mr_opened"
  | "mr_approved"
  | "mr_merged"
  | "mr_rejected"
  | "branch_forked"
  | "comment_reply"
  | "product_update"
  | "tip"
  | "forecast_alert"
  | string;

export type NotificationTab = "all" | "mention" | "activity" | "update";

export interface Notification {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  source_type: string | null;
  source_id: string | null;
  /** When the notification was triggered by a specific comment
   *  (mention or reply), id of that comment. Lets the frontend
   *  scroll to + highlight the exact comment instead of just
   *  landing on the parent surface. */
  comment_id?: string | null;
  actor_user_id: string | null;
  actor_initials: string | null;
  actor_name: string | null;
  read_at: string | null;
  created_at: string;
}

export interface NotificationList {
  items: Notification[];
  unread_count: number;
}

export function fetchNotifications(
  tab: NotificationTab = "all",
  opts: { unreadOnly?: boolean } = {},
): Promise<NotificationList> {
  const params = new URLSearchParams({ tab });
  if (opts.unreadOnly) params.set("unread_only", "true");
  return api<NotificationList>(`/api/v1/notifications?${params.toString()}`);
}

export function fetchUnreadCount(): Promise<{ count: number }> {
  return api<{ count: number }>("/api/v1/notifications/unread-count");
}

/**
 * Quick check: does the user have unread MR / branch activity?
 *
 * Used by the nav to surface a small dot on "Develop" when there's
 * something waiting (MR opened, approval landed, branch forked).
 * Reuses the list endpoint with `tab=activity&unread_only=true` —
 * we only need the boolean, but a count is useful for tooltips.
 */
export async function fetchUnreadActivityCount(): Promise<number> {
  const list = await fetchNotifications("activity", { unreadOnly: true });
  return list.items.length;
}

export function markRead(id: string): Promise<void> {
  return api<void>(`/api/v1/notifications/${id}/read`, { method: "POST" });
}

export function markAllRead(): Promise<{ marked_read: number }> {
  return api<{ marked_read: number }>("/api/v1/notifications/read-all", {
    method: "POST",
  });
}
